import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Error } from '@cognitiv/cicada-ui';
import { getBatchDefinitionParameters } from 'ducks/bacth-definition-parameters/operators';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { IntervalsListState } from 'ducks/intervals-list/slices';
import { selectScheduledBatch } from 'ducks/scheduled-batch/selectors';
import { ScheduledBatchState } from 'ducks/scheduled-batch/slices';
import { updateSettings } from 'ducks/settings/slices';
import { BatchDefinition } from 'ducks/types/store-types';
import { UnionFromArray } from 'ducks/types/type-helpers';
import { useValidate } from 'hooks/useValidation';
import { manage_form_default_state, manage_validation } from 'modals/batches/constants';
import { handleError } from 'utils/errors';

interface ManageScheduleBatchContextType {
  form: ScheduledBatchState;
  setForm: Dispatch<SetStateAction<ScheduledBatchState>>;
  errors: Error[];
  onChange: (item: Partial<ScheduledBatchState>) => void;
  validate: () => boolean;
  onBatchDefinitionSelect: ({ batch_definition_name, batch_definition_id }: BatchDefinition) => Promise<void>;
  onIntervalSelect: (value: UnionFromArray<IntervalsListState>) => void;
  onWeekDaySelect: (items: string, index: number, selected: boolean) => void;
  onFinishDateSelect: (value: { selected_date: string }) => void;
}

interface JobDefinitionProviderProps {
  children: React.ReactNode;
}

export const ManageScheduleBatchContext = createContext<ManageScheduleBatchContextType>(
  {} as ManageScheduleBatchContextType,
);

export function useManageScheduleBatchContext() {
  return useContext(ManageScheduleBatchContext);
}

export const ManageScheduleBatchContextProvider = ({ children }: JobDefinitionProviderProps) => {
  const dispatch = useAppDispatch();
  const scheduled_batch = useAppSelector(selectScheduledBatch);
  const [form, setForm] = useState<ScheduledBatchState>({ ...manage_form_default_state });
  const { errors, validate, resetFieldError } = useValidate(form, manage_validation);

  useEffect(() => {
    setForm({ ...manage_form_default_state, ...scheduled_batch });
  }, [scheduled_batch]);

  const onChange = useCallback(
    (item: Partial<ScheduledBatchState>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onBatchDefinitionSelect = useCallback(
    async ({ batch_definition_name, batch_definition_id }: BatchDefinition) => {
      try {
        if (batch_definition_id) {
          await dispatch(getBatchDefinitionParameters(batch_definition_id));
        }
        onChange({
          batch_definition_name: batch_definition_name,
          batch_definition_id: batch_definition_id,
        });
      } catch (err) {
        handleError(err);
      }

      dispatch(updateSettings({ loading: false }));
    },
    [onChange, dispatch],
  );

  const onIntervalSelect = useCallback(
    (value: UnionFromArray<IntervalsListState>) => {
      onChange({
        interval_name: String(value.interval_name),
        interval_id: Number(value.interval_id),
      });
    },
    [onChange],
  );

  const onWeekDaySelect = useCallback(
    (items: string, index: number, selected: boolean) => {
      const res = [...items];
      res[index] = selected ? '0' : '1';
      onChange({ interval_bitmask: res.join('') });
    },
    [onChange],
  );

  const onFinishDateSelect = useCallback(
    (value: { selected_date: string } | null) => {
      if (value === null) {
        onChange({ finish_datetime_formatted: null });
      } else {
        onChange({ finish_datetime_formatted: value.selected_date });
      }
    },
    [onChange],
  );

  const value = useMemo(
    () => ({
      form,
      setForm,
      errors,
      onChange,
      validate,
      onBatchDefinitionSelect,
      onIntervalSelect,
      onWeekDaySelect,
      onFinishDateSelect,
    }),
    [errors, form, onBatchDefinitionSelect, onChange, onFinishDateSelect, onIntervalSelect, onWeekDaySelect, validate],
  );

  return <ManageScheduleBatchContext.Provider value={value}>{children}</ManageScheduleBatchContext.Provider>;
};
